<template>
  <div class="container max-w-lg">
    <div
      v-if="message"
      class="bg-positive-100 py-2 px-4 mb-6 text-sm text-positive-900 border border-positive-500 rounded"
    >
      <p>
        <icon icon="check" class="w-5 h-5 mr-2 icons-positive align-middle" />
        The profile has been successfully update.
      </p>
    </div>
    <form class="profile" @submit.prevent="update">
      <div class="p-6 bg-white shadow-lg">
        <auth-input
          v-model="auth.name"
          label="Name"
          input-type="text"
          :error="getError('name')"
          :required="true"
          placeholder="Your Name"
          :loading="loading"
          :autocomplete="true"
        />

        <auth-input
          v-model="auth.email"
          label="Email"
          input-type="email"
          :error="getError('email')"
          :required="true"
          placeholder="e.g. your.name@email.com"
          :loading="loading"
          :autocomplete="true"
          :locked="false"
        />

        <auth-input
          v-model="auth.job_title"
          label="Job Title"
          input-type="text"
          :error="getError('job_title')"
          :required="false"
          placeholder="e.g. Managing Director"
          :loading="loading"
          :autocomplete="true"
        />

        <auth-input
          v-model="auth.telephone"
          label="Telephone"
          input-type="tel"
          :error="getError('telephone')"
          :required="false"
          placeholder=""
          :loading="loading"
          :autocomplete="true"
        />

        <div class="pt-2 pb-6">
          <div class="text-center border-b border-grey-200">
            <p class="font-semibold -mb-3">
              <span class="bg-white px-3">Authentication</span>
            </p>
          </div>
        </div>

        <auth-input
          v-model="auth.password"
          label="New Password"
          input-type="password"
          :error="getError('password')"
          :required="false"
          placeholder="Your Password"
          :loading="loading"
          :autocomplete="true"
        />

        <auth-input
          v-model="auth.password_confirmation"
          label="Repeat Password"
          input-type="password"
          :loading="loading"
        />

        <div class="pt-2 pb-6">
          <div class="text-center border-b border-grey-200">
            <p class="font-semibold -mb-3">
              <span class="bg-white px-3">Company Details</span>
            </p>
          </div>
        </div>

        <auth-input
          v-model="company_number"
          label="Company Number"
          input-type="text"
          :required="false"
          placeholder="01234567"
          :maxlength="8"
          :loading="loading"
          :autocomplete="true"
          :locked="true"
        >
          <div
            v-if="Object.keys(company_details).length > 1"
            class="bg-grey-200 -ml-2 -mr-2 -mb-2 mt-2 px-3 py-2 text-black font-light"
          >
            <h2 class="text-xs text-grey-500">
              Your company's details
            </h2>
            <p class="text-sm">
              {{ company_details.name | sentencecase }}
            </p>
            <p class="text-xs">
              {{ companyAddress }}
            </p>
          </div>
        </auth-input>
      </div>
      <div class="flex">
        <div class="w-1/3 md:w-48 mr-3 flex-grow-0">
          <app-button
            type="submit"
            colour="primary"
            class="mt-8 mb-4"
            :disabled="loading"
          >
            <span v-if="!loading">Update</span>
            <span v-else>Updating...</span>
          </app-button>
        </div>
        <div class="w-1/3 md:w-48 flex-grow-0 md:mr-3">
          <app-button
            type="button"
            colour="confirm"
            class="mt-8 mb-4"
            @click="mimic()"
          >
            <span>Mimic</span>
          </app-button>
        </div>
        <div class="w-1/3 md:w-48 flex-grow-0">
          <app-button
            type="button"
            colour="delete"
            class="mt-8 mb-4 border border-negative-500"
            :disabled="loading || state.profile.id === $AuthUser.id"
            @click="deleteUser"
          >
            <span v-if="!loading">Delete</span>
            <span v-else>Deleting...</span>
          </app-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import pick from "lodash/pick";
import { requestMixin } from "../../mixins/request";
export default {
  name: "UserProfile",
  mixins: [requestMixin],
  data() {
    return {
      auth: {
        name: this.state.profile.name || "",
        email: this.state.profile.email,
        telephone: this.state.profile.telephone || "",
        job_title: this.state.profile.job_title || "",
        password: "",
        password_confirmation: ""
      },
      company_number: this.state.profile.company.number,
      message: false,
      loading: false
    };
  },
  computed: {
    company_details() {
      return this.state.profile.company;
    },
    companyAddress() {
      let address = pick(this.company_details, [
        "address_line_1",
        "address_line_2",
        "town",
        "county",
        "postcode"
      ]);
      return Object.values(address)
        .filter(value => value && value.length)
        .join(", ");
    }
  },
  created() {
    document.title = `${this.auth.name}'s Profile :: ${document.title}`;
  },
  methods: {
    mimic() {
      window.location = route("users.impersonate", {
        user: this.state.profile.id
      });
    },
    update() {
      this.loading = true;
      return axios
        .patch(
          route("users.update", {
            user: this.state.profile.id
          }).url(),
          pick(this.auth, [
            "name",
            "email",
            "telephone",
            "job_title",
            "password",
            "password_confirmation"
          ])
        )
        .then(response => this.updateReponse(response))
        .catch(error => this.requestError(error))
        .then(() => (this.loading = false));
    },
    deleteUser() {
      if (confirm("Are you sure you want to delete this user?")) {
        this.loading = true;
        return axios
          .delete(
            route("users.delete", {
              user: this.state.profile.id
            }).url()
          )
          .then(() => Turbolinks.visit(route("users.index")))
          .catch(error => this.requestError(error))
          .then(() => (this.loading = false));
      }
    },
    updateReponse(response) {
      if (response.data.hasOwnProperty("errors")) {
        const error = { response: { ...response } };
        this.requestError(error);
      } else {
        this.auth.password = "";
        this.auth.password_confirmation = "";
        this.message = true;
      }
    }
  }
};
</script>
<style>
.profile .auth-input label {
  @apply bg-white;
}
</style>
